import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {mainQuery} from '@github-ui/react-core/future/main-query'
import {AssigneesResponseSchema} from '@github-ui/dotcom-schema/types/pull_requests/v1/assignees'
import {DevelopmentResponseSchema} from '@github-ui/dotcom-schema/types/pull_requests/v1/development'
import {LabelsResponseSchema} from '@github-ui/dotcom-schema/types/pull_requests/v1/labels'
import {ParticipantsResponseSchema} from '@github-ui/dotcom-schema/types/pull_requests/v1/participants'
import {DeploymentsResponseSchema} from '@github-ui/dotcom-schema/types/pull_requests/v1/deployments'
import {ReviewRequestsResponseSchema} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import {repoAppBuilder} from '@github-ui/repo-app-builder'
import type {OverviewRoutePayload} from './route-payload-types'
import {pageDataQueryDeps, shouldRevalidatePullRequestPageData, withOverviewParam} from './page-data-query'

const overviewRouteOptions = {
  shouldRevalidate: shouldRevalidatePullRequestPageData,
  uiServiceOptions: {
    tsrConfig: {
      // Search parameters omitted from queryDeps do not reload this route in the UI service.
      shouldReload: undefined,
    },
  },
} as const

// When Overview replaces the conversation, it is mounted at the PR index route (`/pull/:number`).
export function buildPullRequestsOverviewRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  // due to how we transition from a standalone route to the index route, we need to keep this route id as `pullRequestsOverviewIndexRoute`
  // this isn't really a problem but does slightly deviate from our usual route name === route id convention
  // this deviation only really shows up in telemetry and creating embedded data for tests
  return appBuilder.createQueryRouteConfig('pullRequestsOverviewIndexRoute', {
    path: '/:owner/:repo/pull/:pr_number',
    index: true,
    queries: [
      mainQuery<OverviewRoutePayload>({
        queryDeps: ({pathname}) => ({pathname: withOverviewParam(pathname, true)}),
      }),
      ...secondaryQueries,
    ],
    ...overviewRouteOptions,
  })
}

// Extracted so both route builders share one definition
// `as const` preserves the literal `queryName`s (otherwise the route type collapses to just `mainQuery`),
// and the explicit `params` type replaces the contextual typing lost by pulling this out of the `createQueryRouteConfig` call.
type OverviewQueryParams = {params: {owner: string; repo: string; pr_number: string}}

const secondaryQueries = [
  // mainQuery omitted here because it needs to be in the route config directly for TS type inference to work correctly.
  {
    queryName: 'review_requests',
    queryDeps: ({params}: OverviewQueryParams) => pageDataQueryDeps({params, name: 'review_requests'}),
    schema: ReviewRequestsResponseSchema,
    persister: {
      enabled: true,
      flag: 'pull_request_persister',
    },
  },
  {
    queryName: 'assignees',
    queryDeps: ({params}: OverviewQueryParams) => pageDataQueryDeps({params, name: 'assignees'}),
    schema: AssigneesResponseSchema,
    persister: {
      enabled: true,
      flag: 'pull_request_persister',
    },
  },
  {
    queryName: 'development',
    queryDeps: ({params}: OverviewQueryParams) => pageDataQueryDeps({params, name: 'development'}),
    schema: DevelopmentResponseSchema,
    persister: {
      enabled: true,
      flag: 'pull_request_persister',
    },
  },
  {
    queryName: 'labels',
    queryDeps: ({params}: OverviewQueryParams) => pageDataQueryDeps({params, name: 'labels'}),
    schema: LabelsResponseSchema,
    persister: {
      enabled: true,
      flag: 'pull_request_persister',
    },
  },
  {
    queryName: 'participants',
    queryDeps: ({params}: OverviewQueryParams) => pageDataQueryDeps({params, name: 'participants'}),
    schema: ParticipantsResponseSchema,
    persister: {
      enabled: true,
      flag: 'pull_request_persister',
    },
  },
  {
    queryName: 'deployments',
    queryDeps: ({params}: OverviewQueryParams) => pageDataQueryDeps({params, name: 'deployments'}),
    schema: DeploymentsResponseSchema,
    persister: {
      enabled: true,
      flag: 'pull_request_persister',
    },
  },
] as const

export const pullRequestsOverviewRoute = buildPullRequestsOverviewRoute(repoAppBuilder)
